<template>
  <div class="catalog-product-modal">
    <div class="catalog-product-main-wrapper">
      <div
        class="error-text"
        v-if="
          hasInvalidPage && Array.isArray(getRows) && getRows.length && images
        "
      >
        <img src="@/assets/images/icons/alert-circle.svg" />
        Tablodaki eksik veya yanlış girdiğiniz bilgileri düzenleyin. Varsa,
        tablonun diğer sayfalarını da kontrol edin.
      </div>

      <SingleProductTable
        v-show="getRowsCount"
        ref="productDetailTableRef"
        :variants="tableRows"
        :attributes="props.attributes"
        :columns="columnList"
        :selectedCurrency="getSelectedCurrency"
        :pinnedColumns="pinnedColumns"
      />

      <RequestUpdateModalForBarcode
        ref="barcodeModalRef"
        :selectedRowData="selectedRowData"
        :selectedGtin="selectedGtin"
        :productDetailTableRef="productDetailTableRef"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, watch } from "vue";
import { useStore } from "vuex";
import useColumnService from "../services/getCatalogColumns";
import SingleProductTable from "@/components/Table/SingleProductTable";
import RequestUpdateModalForBarcode from "./Modals/RequestUpdateModalForBarcode.vue";
import { sendProductDetailEvent } from "@/utils/ga4/helper";
import { GA4_EVENT } from "@/utils/ga4/event";

const props = defineProps({
  attributes: { type: Function, default: () => {} },
  category: { type: [Number, null], required: true },
  selectedCurrency: { type: Function, required: true, default: null },
  variantRefs: { type: Function, required: true, default: () => {} },
  tableData: { type: Array, default: () => [] },
  isReadyToSave: { type: Boolean, default: false },
  isSaved: { type: Boolean, required: true, default: false },
  productId: { type: [String, Number], required: false, default: '' },
});

const emit = defineEmits(["update:isReadyToSave"]);

const store = useStore();
const tableRows = ref([]);
const productDetailTableRef = ref();
const columnList = ref([]);
const validateRef = ref(null);
const barcodeModalRef = ref(null);
const selectedRowData = ref(null);
const selectedGtin = ref(null);

const pinnedColumns = {
  left: ['select-col', 'name', 'images'],
  right: ['actions']
}

watch(() => props.tableData, (newData) => {
  if (newData && newData.length > 0) {
    tableRows.value = [...newData];
  }
}, { immediate: true });

if (props.tableData.length > 0) {
  tableRows.value = [...props.tableData];
} else {
  console.log('No initial mock data found, tableRows remains empty');
}

const resetDirtyState = () => {
  const tableData = productDetailTableRef.value?.table?.options?.meta.getData();
  if (!tableData || tableData.length === 0) return;
  
  tableData.forEach(row => {
    if (!row.validations) return;
    Object.keys(row.validations).forEach(key => {
      if (!row.validations[key]) return;
      row.validations[key].isDirty = false;
    });
  });
  
  tableRows.value = [...tableRows.value]; 
  emit("update:isReadyToSave", false);
};

const revertStockAndDurationToInitial = () => {
  const tableData = productDetailTableRef.value?.table?.options?.meta.getData();

  if (!tableData || tableData.length === 0 || !Array.isArray(props.tableData)) {
    return;
  }

  const initialById = new Map(
    (props.tableData || [])
      .filter((row) => row && row.id !== undefined && row.id !== null)
      .map((row) => [row.id, row]),
  );

  tableData.forEach((row) => {
    const initial = initialById.get(row.id);
    if (!initial) return;

    row.stock = initial.stock;
    row.duration = initial.duration;

    if (row.validations) {
      ["stock", "duration"].forEach((field) => {
        if (row.validations[field]) {
          if (Object.prototype.hasOwnProperty.call(row.validations[field], "valid")) {
            row.validations[field].valid = true;
          }
          if (Object.prototype.hasOwnProperty.call(row.validations[field], "error")) {
            row.validations[field].error = false;
          }
        }
      });
    }
  });

  tableRows.value = [...tableData];
};

watch(
  () => props.isSaved, (newValue) => {
    if (newValue) {
      resetDirtyState();
    }
  }
);

const getCategoryId = computed(() => props.category);
const getSelectedCurrency = computed(() => props.selectedCurrency);
const getRowsCount = computed(() => tableRows.value?.length);
const images = computed(() => store.getters["common/getHasImageError"]);
const hasInvalidPage = computed(() =>
  productDetailTableRef.value?.getInvalidPageNumbers()?.length === 0
    ? false
    : true
);

const getRows = computed(() =>
  productDetailTableRef.value?.table?.options?.meta.getData()
);

const getTableRawData = () => {
  if (productDetailTableRef.value) {
    return productDetailTableRef.value.getRawData();
  }
  
  return [];
};

const isReadyToSave = computed(() => {
  const data = productDetailTableRef.value?.table?.options?.meta.getData();
  if (!data || data.length === 0) return false;
  let isAnyDirty = false;

  for (const row of data) {
    const requiredFields = Object.keys(row.validations);
    for (const field of requiredFields) {
      const value = row[field];
      if (value === undefined || value === null || value === "") {
        validateRef.value?.(value, field, row, true, data); 
        return false;
      }
      const validation = row.validations[field];
      if (validation.valid === false) {
        return false;
      }
      if (validation.isDirty) {
        isAnyDirty = true;
      }
    }
  }

  return isAnyDirty;
});

watch(isReadyToSave, (newValue) => {
  emit("update:isReadyToSave", newValue)
});

const handleBarcodeClick = (rowData, currentGtin) => {
  const buttonName = currentGtin ? 'Değiştir' : 'Ekle';
  const buttonType = currentGtin ? 'Change' : 'Add';

  sendProductDetailEvent(GA4_EVENT.BUTTON_CLICK, {
    button_name: buttonName,
    button_type: buttonType,
    button_area: 'Order and Variant Information',
    item_id: String(props.productId || ''),
  });

  selectedRowData.value = rowData;
  selectedGtin.value = currentGtin;
  
  if (barcodeModalRef.value) {
    barcodeModalRef.value.openModal();
  }
};

watch(
  [getCategoryId, getSelectedCurrency], 
  ([newId, newCurrency]) => {
    if (!newId || !newCurrency) return;

    const { columns, validate } = useColumnService(newId, newCurrency, handleBarcodeClick);
    validateRef.value = validate;
    columnList.value = columns;
  }, 
  { immediate: true }
);

defineExpose({
  getVariantsToSave: getTableRawData,
  validateRef,
  isReadyToSave,
  resetDirtyState,
  revertStockAndDurationToInitial,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
.catalog-product-main-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;

  .error-text {
    padding: 16px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    font-size: 13px;
    line-height: 18px;
    color: $colorN80;
    background: $colorE05;
    border-radius: 6px;
    align-items: center;
    letter-spacing: -0.05px;

    img {
      width: 22px;
      height: 22px;
    }
  }
}
</style>
